import { ActivityPreview } from "@/components/ActivityFeed";
import { ProcessingStatusHeader } from "@/components/ProcessingStatus";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Clock, Download, FileText, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/knowledge-doc/$id")({
	component: KnowledgeDocPage,
});

const aiClient = createClient(AIService, transport);
const mediaClient = createClient(MediaService, transport);

function KnowledgeDocPage() {
	const { id } = Route.useParams();
	const { t } = useTranslation("knowledge");

	// No polling needed — real-time engine
	// invalidates ["knowledge-documents"] via
	// NATS → WS → useRealtime
	const { data: allDocs, isLoading } = useQuery({
		queryKey: ["knowledge-documents"],
		queryFn: () =>
			aiClient.listKnowledgeDocuments({
				page: {
					pageSize: 200,
					pageToken: "",
				},
			}),
	});

	const doc = allDocs?.documents?.find((d) => d.id === id);

	useBreadcrumbs([
		{
			label: t("breadcrumb_settings"),
			href: "/settings",
		},
		{
			label: t("breadcrumb_knowledge"),
			href: "/settings/knowledge",
		},
		{ label: doc?.title || t("document_default") },
	]);

	if (isLoading) {
		return (
			<div className="flex items-start gap-6 px-4 py-4">
				<div className="flex-1 space-y-4">
					<Skeleton className="h-8 w-64" />
					<Skeleton className="h-48 w-full" />
				</div>
				<div className="w-72 shrink-0 space-y-4">
					<Skeleton className="h-32 w-full" />
				</div>
			</div>
		);
	}

	if (!doc) {
		return (
			<div className="py-16 text-center text-sm text-muted-foreground">
				{t("doc_not_found")}
			</div>
		);
	}

	return (
		<div className="flex items-start gap-6 px-4 py-4">
			{/* Left — main content */}
			<div className="flex-1 min-w-0 space-y-6">
				{/* Header */}
				<div className="flex items-center gap-3">
					<Link
						to="/settings/knowledge"
						className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
					>
						<ArrowLeft className="h-4 w-4" />
					</Link>
					<h1 className="text-lg font-semibold truncate flex-1">
						{doc.title || doc.filename}
					</h1>
					<ProcessingStatusHeader state={doc.status} />
				</div>

				{/* Error */}
				{doc.errorMessage && (
					<div className="rounded-lg bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
						{doc.errorMessage}
					</div>
				)}

				{/* Chunks */}
				{doc.status === "ready" && <ChunkList documentId={id} />}

				{doc.status === "processing" && (
					<div className="flex items-center gap-3 py-12 justify-center text-muted-foreground">
						<Loader2 className="h-5 w-5 animate-spin" />
						<span className="text-sm">{t("processing")}</span>
					</div>
				)}

				{doc.status === "pending" && (
					<div className="flex items-center gap-3 py-12 justify-center text-muted-foreground">
						<Clock className="h-5 w-5" />
						<span className="text-sm">{t("pending")}</span>
					</div>
				)}
			</div>

			{/* Right — sidebar */}
			<div className="w-72 shrink-0 space-y-4">
				{/* Info */}
				<Card className="py-3 gap-2">
					<div className="px-4 space-y-2">
						<Label>{t("info_label")}</Label>
						<div className="space-y-1.5 text-sm">
							<div className="flex items-center justify-between">
								<span className="text-muted-foreground">{t("file_label")}</span>
								<span className="truncate ml-2 max-w-[140px]">
									{doc.filename}
								</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-muted-foreground">
									{t("chunks_label")}
								</span>
								<span className="tabular-nums">{doc.chunkCount || "—"}</span>
							</div>
							{doc.createdAt && (
								<div className="flex items-center justify-between">
									<span className="text-muted-foreground">
										{t("uploaded_label")}
									</span>
									<span>
										{doc.createdAt.toDate().toLocaleDateString("ru-RU", {
											day: "2-digit",
											month: "2-digit",
											year: "2-digit",
										})}
									</span>
								</div>
							)}
							{doc.updatedAt && (
								<div className="flex items-center justify-between">
									<span className="text-muted-foreground">
										{t("updated_label")}
									</span>
									<span>
										{doc.updatedAt.toDate().toLocaleDateString("ru-RU", {
											day: "2-digit",
											month: "2-digit",
											year: "2-digit",
											hour: "2-digit",
											minute: "2-digit",
										})}
									</span>
								</div>
							)}
						</div>
					</div>
				</Card>

				{/* Download */}
				{doc.mediaId && <DownloadButton mediaId={doc.mediaId} />}

				{/* Activity */}
				<Card className="py-3 gap-2">
					<div className="px-4">
						<ActivityPreview entityType="knowledge" entityId={id} />
					</div>
				</Card>
			</div>
		</div>
	);
}

function DownloadButton({
	mediaId,
}: {
	mediaId: string;
}) {
	const { t } = useTranslation("knowledge");
	const { data } = useQuery({
		queryKey: ["media-url", mediaId],
		queryFn: () =>
			mediaClient.batchGetMediaUrls({
				mediaIds: [mediaId],
			}),
		enabled: !!mediaId,
	});

	const url = data?.urls?.[0]?.url;

	return (
		<Button
			variant="outline"
			size="sm"
			className="w-full"
			onClick={() => {
				if (url) window.open(url, "_blank");
			}}
			disabled={!url}
		>
			<Download className="h-3.5 w-3.5 mr-1.5" />
			{t("download")}
		</Button>
	);
}

function ChunkList({
	documentId,
}: {
	documentId: string;
}) {
	const { t } = useTranslation("knowledge");
	const { data, isLoading } = useQuery({
		queryKey: ["knowledge-chunks", documentId],
		queryFn: () =>
			aiClient.getKnowledgeChunks({
				documentId,
			}),
	});

	const chunks = data?.chunks ?? [];

	if (isLoading) {
		return (
			<div className="space-y-2">
				<Skeleton className="h-20 w-full" />
				<Skeleton className="h-20 w-full" />
			</div>
		);
	}

	if (chunks.length === 0) {
		return <p className="text-sm text-muted-foreground">{t("no_chunks")}</p>;
	}

	return (
		<div className="space-y-3">
			<h2 className="text-sm font-medium">
				{t("content_title", { count: chunks.length })}
			</h2>
			{chunks.map((chunk) => (
				<div key={chunk.id} className="rounded-lg border p-4 space-y-1">
					<div className="flex items-center gap-2 text-xs text-muted-foreground">
						<FileText className="h-3 w-3" />
						<span className="font-mono">
							{t("chunk_label", { index: chunk.chunkIndex + 1 })}
						</span>
					</div>
					<p className="text-sm whitespace-pre-wrap leading-relaxed">
						{chunk.content}
					</p>
				</div>
			))}
		</div>
	);
}
